import React, { useState } from "react";

import cx from "classnames";

import { doNothing, setStateValue } from "@kazoohr/helpers";

import { Divider } from "../Divider";
import { Icon } from "../Icon";

import type { MenuItem } from "./types";
import styles from "./UserMenu.scss";

interface UserNavProps {
  hasSignOut?: boolean;
  isOpen?: boolean;
  menuItems?: MenuItem[];
  onSignOut?: () => void;
  renderLink?: (item: MenuItem) => React.ReactNode;
  signOutText?: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function DefaultRender(item: MenuItem): React.ReactElement {
  return item.openInNewTab ? (
    <a href={item.url} target="_blank" rel="noopener noreferrer">
      {item.text}
    </a>
  ) : (
    <a href={item.url}>{item.text}</a>
  );
}

export function UserNav({
  hasSignOut,
  isOpen,
  menuItems = [],
  onSignOut = doNothing,
  renderLink: propRenderLink,
  signOutText = "Sign out",
  setIsOpen,
}: UserNavProps): React.ReactElement {
  const [openItem, setOpenItem] = useState<number | undefined>(undefined);

  const renderLink = propRenderLink || DefaultRender;

  function toggleItem(keyId: number): void {
    setOpenItem(openItem === keyId ? undefined : keyId);
  }

  function isItemOpen(keyId: number): boolean {
    return openItem === keyId;
  }

  const className = cx({
    [styles["user-menu__menu"]]: true,
    [styles["user-menu__menu--open"]]: isOpen,
  });

  return (
    <nav className={className} data-testid="user-menu__menu">
      <ul className={styles["user-menu__menu__item"]}>
        {menuItems.map((item, key) => {
          if (item.subItems) {
            return (
              <li className={styles["sub-item-container"]} key={key}>
                <button
                  className={cx({
                    [styles["sub-button"]]: true,
                    [styles["sub-button-open"]]: isItemOpen(key),
                  })}
                  onClick={() => toggleItem(key)}
                >
                  {item.text}
                  <span className={styles["item-with-sub-item-icon"]}>
                    <Icon
                      icon={isItemOpen(key) ? "caret_up" : "caret_dn"}
                      size={12}
                      color={styles.defaultText}
                    />
                  </span>
                </button>
                {isItemOpen(key) && (
                  <ul>
                    {item.subItems.map((subItem, subKey) => (
                      <li
                        data-testid="user-menu__item"
                        key={subKey}
                        className={styles["user-menu__menu__sub_item"]}
                        onClick={setStateValue(setIsOpen, false)}
                      >
                        {renderLink(subItem)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          }
          return (
            <li
              data-testid="user-menu__item"
              className={styles["no-sub-item"]}
              key={key}
              onClick={setStateValue(setIsOpen, false)}
            >
              {renderLink(item)}
            </li>
          );
        })}
        {hasSignOut && (
          <>
            <Divider className={styles["nav-divider"]} />
            <button
              className={styles["sign-out-button"]}
              data-testid="sign-out-button"
              onClick={onSignOut}
            >
              {signOutText}
            </button>
          </>
        )}
      </ul>
    </nav>
  );
}
